import * as React from "react";
import ReactDOM from "react-dom";
import { ComboBox } from "react-ui";
import { MenuItemState } from "@skbkontur/react-ui/components/MenuItem";

import { iterateEntries } from "../../../../../Common/IterableUtils";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { nullElement, pullContext } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { IScrollToContext, scrollToContextTypes } from "../../Internals/Contexts/ScrollToContext";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ExternalBindingPath } from "../../../Engine/Core/CoreTypes";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { isDOMRectInView } from "../../../Helpers/BrowserHelpers";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";
import { splitHighlightedParts } from "../../../Helpers/StringHelpers";
import { ReadonlyValueWrapper } from "../ComboBox/ComboBox.styles";
import { Text } from "../../ValueViewers/Text/Text";

import * as styles from "./CustomSuggestComboBox.styles";

export interface CustomSuggestComboBoxProps extends ControlValidationProps, ControlTooltipProps {
    bindingPath: AbsoluteModelPath;
    value: Item | undefined;
    onChange: (value: string | undefined) => void;
    allowTextWrap?: boolean;
    disablePortal?: boolean;
    keyColWidth?: number;
    onBlur?: () => void;
    onFocus?: () => void;
    onMouseEnter?: (event: React.SyntheticEvent) => void;
    onMouseLeave?: (event: React.SyntheticEvent) => void;
    placeholder?: string;
    setInfo: (path: ExternalBindingPath, payload: string) => void;
    width?: string | number;
    disabled?: boolean;
    onQuery: (search: string) => Promise<Item[]>;
    isReadOnly?: boolean;
}

interface CustomSuggestComboBoxState {
    isFocused: boolean;
    isHovered: boolean;
    search: string;
}

export interface Item {
    displayHint?: string;
    displayKey?: string;
    displayValue: string;
    payload?: { [key: string]: string };
}

export function selectSearchText(text: string, search: string): React.ReactNode[] {
    const parts = splitHighlightedParts(text, search);
    return parts.map((it, index) => (it.highlighted ? <strong key={index}>{it.text}</strong> : it.text));
}

export class CustomSuggestComboBox extends React.Component<CustomSuggestComboBoxProps, CustomSuggestComboBoxState> {
    public static defaultProps = {
        allowTextWrap: true,
        disablePortal: true,
        keyColWidth: 60,
        width: "100%",
        value: { displayValue: "", payload: {} },
    };

    public state: CustomSuggestComboBoxState = {
        isFocused: false,
        isHovered: false,
        search: "",
    };

    public static contextTypes = scrollToContextTypes;

    private combobox: ComboBox<Item> | null = nullElement;

    public render(): JSX.Element {
        const { error, placeholder, tooltipWidth, value } = this.props;
        const { isFocused, isHovered } = this.state;
        const tooltipTrigger = error && (isFocused || isHovered) ? "opened" : "closed";
        const tooltipPosition = this.props.tooltipPosition ?? "top left";

        if (this.props.isReadOnly) {
            return (
                <ReadonlyValueWrapper>
                    <Text bindingPath={this.props.bindingPath} value={value?.displayValue} />
                </ReadonlyValueWrapper>
            );
        }

        return (
            <TestPageObjectHandle
                id={this.props.bindingPath}
                pageObject={{
                    getType: () => "CustomSuggestComboBox",
                    isFocused: () =>
                        document.activeElement ===
                        toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.querySelector("input"),
                    isInView: () =>
                        isDOMRectInView(toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.getBoundingClientRect()),
                    getValue: () => this.props.value,
                    setValue: (nextValue: string = "") => {
                        this.props.onChange(nextValue);
                    },
                }}>
                <FocusManagementObserver onRequestFocus={this.handleRequestFocus}>
                    <Tooltip pos={tooltipPosition} trigger={tooltipTrigger} value={error} width={tooltipWidth}>
                        <ComboBox<Item>
                            getItems={this.getItems}
                            ref={this.comboboxRef}
                            value={value}
                            error={Boolean(error)}
                            placeholder={placeholder}
                            drawArrow={false}
                            itemToValue={this.valueToString}
                            valueToString={this.valueToString}
                            renderItem={this.renderItem}
                            renderValue={this.valueToString}
                            onValueChange={this.handleChange}
                            onUnexpectedInput={this.handleUnexpectedInput}
                            disabled={this.props.disabled}
                            width={this.props.width}
                            onBlur={this.handleBlur}
                            onFocus={this.handleFocus}
                            onMouseEnter={this.handleMouseEnter}
                            onMouseLeave={this.handleMouseLeave}
                        />
                    </Tooltip>
                </FocusManagementObserver>
            </TestPageObjectHandle>
        );
    }

    private readonly handleRequestFocus = (): void => {
        if (this.combobox != undefined) {
            const comboboxNode = ReactDOM.findDOMNode(this.combobox);
            if (comboboxNode && comboboxNode instanceof Element) {
                pullContext<IScrollToContext>(this).scrollTo(comboboxNode);
                this.combobox.focus();
            }
        }
    };

    private readonly comboboxRef = (combobox: ComboBox<Item>) => (this.combobox = combobox);

    private readonly getItems = (search: string): Promise<Item[]> =>
        this.props.onQuery(search).then(data => {
            this.setState({ search: search });

            return data;
        });

    private readonly valueToString = (value: Item): string => value.displayKey ?? value.displayValue;

    private readonly handleChange = (value: Item) => {
        if (value.payload) {
            for (const [path, infoValue] of iterateEntries(value.payload)) {
                this.props.setInfo(path, infoValue);
            }
        } else {
            this.props.onChange(value.displayValue);
        }
    };

    private readonly handleUnexpectedInput = (query: string): Item | undefined | null => ({
        displayValue: query,
    });

    private readonly handleFocus = () => {
        this.setState({ isFocused: true });
        this.props.onFocus?.();
    };

    private readonly handleBlur = () => {
        this.setState({ isFocused: false });
        this.props.onBlur?.();
    };

    private readonly handleMouseEnter = (event: React.SyntheticEvent) => {
        this.setState({ isHovered: true });
        this.props.onMouseEnter?.(event);
    };

    private readonly handleMouseLeave = (event: React.SyntheticEvent) => {
        this.setState({ isHovered: false });
        this.props.onMouseLeave?.(event);
    };

    private readonly renderItem = (value: Item, state?: MenuItemState): React.ReactNode => {
        const { search } = this.state;
        const { displayHint, displayKey, displayValue = "" } = value;
        const { keyColWidth } = this.props;

        let hint: JSX.Element | null = nullElement;

        if (displayHint) {
            hint = (
                <div css={styles.hint} className={state === "hover" ? "hover" : undefined}>
                    {selectSearchText(displayHint, search)}
                </div>
            );
        }

        return (
            <div css={styles.item} className={!this.props.allowTextWrap ? "no-wrap" : undefined}>
                {displayKey ? (
                    <div css={styles.container}>
                        <div style={{ width: `${keyColWidth}px` }}>{selectSearchText(displayKey, search)}</div>
                        <div css={styles.name}>
                            <div>{selectSearchText(displayValue, search)}</div>
                            {hint}
                        </div>
                    </div>
                ) : (
                    <div>
                        <span>{selectSearchText(displayValue, search)}</span>
                        {hint}
                    </div>
                )}
            </div>
        );
    };
}
